/**
 * Balans - Balans per peildatum
 * EXACT gekopieerd van Morris templates/balans.html
 */

import { useState, useEffect, useCallback } from 'react';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, Balans as BalansData } from '../../services/boekhouding.service';

const Balans = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BalansData | null>(null);
  // Peildatum state - EXACT Morris
  const [peildatum, setPeildatum] = useState<string>('');

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  const loadData = useCallback(async (peil?: string) => {
    setLoading(true);
    try {
      const result = await boekhoudingService.getBalans(huidigJaar, peil);
      setData(result);
      // Update peildatum van response - EXACT Morris
      if (result.peildatum) {
        setPeildatum(result.peildatum);
      }
    } catch (err) {
      console.error('Error loading balans:', err);
    } finally {
      setLoading(false);
    }
  }, [huidigJaar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handler voor peildatum wijziging - EXACT Morris
  const handlePeildatumChange = (e: React.FormEvent) => {
    e.preventDefault();
    loadData(peildatum);
  };

  // Reset naar vandaag - EXACT Morris
  const handleReset = () => {
    setPeildatum('');
    loadData();
  };

  const formatBedrag = (bedrag: number | undefined): string => {
    if (bedrag === undefined || bedrag === null) return '';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(bedrag) || 0);
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Balans per 31-12-${huidigJaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) {
    return (
      <BoekhoudingLayout title={`Balans per 31-12-${huidigJaar}`}>
        <div className={styles.emptyState}>Geen data gevonden</div>
      </BoekhoudingLayout>
    );
  }

  // Bereken totaal vaste activa
  const totaalVasteActiva = Object.values(data.activa.vaste_activa).reduce((sum, v) => sum + v, 0);

  // Bereken totalen - EXACT Morris
  const totaalActiva = data.activa.bank + data.activa.kas + data.activa.debiteuren +
    (data.activa.btw_vordering || 0) + totaalVasteActiva;
  const totaalPassiva = data.passiva.kapitaal + data.passiva.oudedagreserve + data.passiva.crediteuren +
    (data.passiva.prive_km || 0) + (data.passiva.lening_ford_transit || 0) + (data.passiva.btw_schuld || 0) + (data.passiva.extra_btw_auto || 0);

  // Beginbalans totalen - EXACT Morris logica
  const beginActiva = data.beginbalans?.activa || {};
  const beginPassiva = data.beginbalans?.passiva || {};

  // Morris berekening met correcte BTW behandeling
  // Omzetbelasting in activa kan negatief zijn (schuld) of positief (vordering)
  const overige_vord = beginActiva['Overige vorderingen'] || 0;
  const btw_activa = beginActiva['Omzetbelasting'] || 0;  // Kan negatief zijn!
  const btw_schuld_passiva = beginPassiva['Omzetbelasting'] && beginPassiva['Omzetbelasting'] > 0 ? beginPassiva['Omzetbelasting'] : 0;
  const oudedag = beginPassiva['Oudedagsreserve'] || beginPassiva['Oudedagreserve'] || 0;
  const ford_transit = beginActiva['Ford Transit'] || 0;
  const lening_ford = beginPassiva['Lening Ford Transit'] || 0;
  const extra_btw_auto = beginPassiva['BTW privé gebruik auto'] || beginPassiva['Extra BTW auto'] || 0;

  // De beginbalans staat op naam zoals hij ooit is ingetypt: de huidige banknaam,
  // of "Bank"/"Rabobank" uit een oudere administratie.
  const banknaam = data.banknaam || 'Bank';
  const beginBank = beginActiva[banknaam] ?? beginActiva['Bank'] ?? beginActiva['Rabobank'] ?? 0;

  const totaalBeginActiva = beginBank + (beginActiva['Kas'] || 0) +
    (beginActiva['Debiteuren'] || 0) + (beginActiva['Gereedschap'] || 0) + (beginActiva['Kantoor'] || 0) +
    ford_transit + overige_vord + btw_activa;  // btw_activa kan negatief zijn
  const totaalBeginPassiva = (beginPassiva['Kapitaal'] || 0) + oudedag +
    (beginPassiva['Crediteuren'] || 0) + lening_ford + btw_schuld_passiva + extra_btw_auto;

  // Format peildatum voor titel - EXACT Morris
  const formatPeildatumTitel = () => {
    if (peildatum) {
      const d = new Date(peildatum);
      return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
    }
    return `31-12-${huidigJaar}`;
  };

  return (
    <BoekhoudingLayout title={`Balans per ${formatPeildatumTitel()}`}>
      {/* Peildatum selector - EXACT Morris */}
      <div className={styles.card} style={{ marginBottom: '1rem', padding: '0.75rem' }}>
        <form onSubmit={handlePeildatumChange} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <label htmlFor="peildatum" style={{ fontWeight: 500 }}>Peildatum:</label>
          <input
            type="date"
            id="peildatum"
            value={peildatum}
            onChange={(e) => setPeildatum(e.target.value)}
            style={{ padding: '0.35rem', border: '1px solid var(--gray-300)', borderRadius: '4px' }}
          />
          <button type="submit" className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}>
            Toepassen
          </button>
          <button type="button" onClick={handleReset} className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}>
            Reset
          </button>
        </form>
      </div>

      {/* EXACT Morris balans.html structuur */}
      <div className={styles.balansContainer}>
        {/* Eindbalans */}
        <div className={styles.card}>
          <h3 className={styles.balansHeader}>BALANS PER {formatPeildatumTitel()}</h3>
          <table className={styles.balansTable}>
            <thead>
              <tr>
                <th></th>
                <th className={styles.alignRight}>Activa</th>
                <th className={styles.alignRight}>Passiva</th>
              </tr>
            </thead>
            <tbody>
              {/* Liquide middelen */}
              <tr>
                <td>{banknaam}</td>
                <td className={styles.alignRight}>{formatBedrag(data.activa.bank)}</td>
                <td></td>
              </tr>
              <tr>
                <td>Kas</td>
                <td className={styles.alignRight}>{formatBedrag(data.activa.kas)}</td>
                <td></td>
              </tr>
              {data.passiva.lening_ford_transit > 0 && (
                <tr>
                  <td>Lening Ford Transit</td>
                  <td></td>
                  <td className={styles.alignRight}>{formatBedrag(data.passiva.lening_ford_transit)}</td>
                </tr>
              )}

              {/* Lege rij */}
              <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

              {/* Vaste activa */}
              {data.activa.vaste_activa['Gereedschap'] && (
                <tr>
                  <td>Gereedschap</td>
                  <td className={styles.alignRight}>{formatBedrag(data.activa.vaste_activa['Gereedschap'])}</td>
                  <td></td>
                </tr>
              )}
              {data.activa.vaste_activa['Kantoor'] && (
                <tr>
                  <td>Kantoor</td>
                  <td className={styles.alignRight}>{formatBedrag(data.activa.vaste_activa['Kantoor'])}</td>
                  <td></td>
                </tr>
              )}
              {data.activa.vaste_activa['Ford Transit'] && (
                <tr>
                  <td>Ford Transit</td>
                  <td className={styles.alignRight}>{formatBedrag(data.activa.vaste_activa['Ford Transit'])}</td>
                  <td></td>
                </tr>
              )}

              {/* Lege rij */}
              <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

              {/* Debiteuren / Crediteuren */}
              <tr>
                <td>Crediteuren</td>
                <td></td>
                <td className={styles.alignRight}>{formatBedrag(data.passiva.crediteuren)}</td>
              </tr>
              {(data.passiva.prive_km || 0) > 0 && (
              <tr>
                <td>Autokosten</td>
                <td></td>
                <td className={styles.alignRight}>{formatBedrag(data.passiva.prive_km)}</td>
              </tr>
              )}
              <tr>
                <td>Debiteuren</td>
                <td className={styles.alignRight}>{formatBedrag(data.activa.debiteuren)}</td>
                <td></td>
              </tr>

              {/* Lege rij */}
              <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

              {/* BTW */}
              {data.activa.btw_vordering > 0 && (
                <tr>
                  <td>Omzetbelasting</td>
                  <td className={styles.alignRight}>{formatBedrag(data.activa.btw_vordering)}</td>
                  <td></td>
                </tr>
              )}
              {data.passiva.btw_schuld > 0 && (
                <tr>
                  <td>Omzetbelasting</td>
                  <td></td>
                  <td className={styles.alignRight}>{formatBedrag(data.passiva.btw_schuld)}</td>
                </tr>
              )}
              {data.passiva.extra_btw_auto > 0 && (
                <tr>
                  <td>BTW privé gebruik auto</td>
                  <td></td>
                  <td className={styles.alignRight}>{formatBedrag(data.passiva.extra_btw_auto)}</td>
                </tr>
              )}

              {/* Lege rij */}
              <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

              {/* Eigen vermogen */}
              <tr>
                <td>Oudedagsreserve</td>
                <td></td>
                <td className={styles.alignRight}>{formatBedrag(data.passiva.oudedagreserve)}</td>
              </tr>
              <tr>
                <td>Kapitaal</td>
                <td></td>
                <td className={styles.alignRight}>{formatBedrag(data.passiva.kapitaal)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className={styles.separatorRow}>
                <td></td>
                <td className={styles.alignRight}>========</td>
                <td className={styles.alignRight}>========</td>
              </tr>
              <tr className={styles.totaalRow}>
                <td></td>
                <td className={styles.alignRight}><strong>{formatBedrag(totaalActiva)}</strong></td>
                <td className={styles.alignRight}><strong>{formatBedrag(totaalPassiva)}</strong></td>
              </tr>
              {data.verschil !== 0 && (
                <tr className={styles.verschilRow}>
                  <td>Verschil</td>
                  <td colSpan={2} className={`${styles.alignRight} ${styles.verschilFout}`}>
                    {formatBedrag(data.verschil)}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>

        {/* Bijtelling auto's - EXACT Morris */}
        {data.autos && data.autos.length > 0 && data.autos.map(auto => (
          <div key={auto.id} className={styles.card} style={{ marginTop: '2rem' }}>
            <h3 className={styles.balansHeader}>Bijtelling {auto.naam}</h3>
            <BijtellingTabel auto={auto} jaar={huidigJaar} peildatum={peildatum} />
          </div>
        ))}

        {/* Beginbalans */}
        {data.beginbalans && Object.keys(data.beginbalans.activa).length > 0 && (
          <div className={styles.card} style={{ marginTop: '2rem' }}>
            <h3 className={styles.balansHeader}>BALANS PER 1-1-{data.beginbalans.jaar}</h3>
            <table className={styles.balansTable}>
              <thead>
                <tr>
                  <th></th>
                  <th className={styles.alignRight}>Activa</th>
                  <th className={styles.alignRight}>Passiva</th>
                </tr>
              </thead>
              <tbody>
                {/* Liquide middelen */}
                <tr>
                  <td>{banknaam}</td>
                  <td className={styles.alignRight}>{formatBedrag(beginBank)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Kas</td>
                  <td className={styles.alignRight}>{formatBedrag(beginActiva['Kas'])}</td>
                  <td></td>
                </tr>
                {beginPassiva['Lening Ford Transit'] && (
                  <tr>
                    <td>Lening Ford Transit</td>
                    <td></td>
                    <td className={styles.alignRight}>{formatBedrag(beginPassiva['Lening Ford Transit'])}</td>
                  </tr>
                )}

                <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

                {/* Vaste activa */}
                {beginActiva['Gereedschap'] && (
                  <tr>
                    <td>Gereedschap</td>
                    <td className={styles.alignRight}>{formatBedrag(beginActiva['Gereedschap'])}</td>
                    <td></td>
                  </tr>
                )}
                {beginActiva['Kantoor'] && (
                  <tr>
                    <td>Kantoor</td>
                    <td className={styles.alignRight}>{formatBedrag(beginActiva['Kantoor'])}</td>
                    <td></td>
                  </tr>
                )}
                {beginActiva['Ford Transit'] && (
                  <tr>
                    <td>Ford Transit</td>
                    <td className={styles.alignRight}>{formatBedrag(beginActiva['Ford Transit'])}</td>
                    <td></td>
                  </tr>
                )}

                <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

                {/* Debiteuren / Crediteuren */}
                <tr>
                  <td>Crediteuren</td>
                  <td></td>
                  <td className={styles.alignRight}>{formatBedrag(beginPassiva['Crediteuren'] || 0)}</td>
                </tr>
                <tr>
                  <td>Debiteuren</td>
                  <td className={styles.alignRight}>{formatBedrag(beginActiva['Debiteuren'])}</td>
                  <td></td>
                </tr>
                {beginActiva['Overige vorderingen'] && (
                  <tr>
                    <td>Overige vorderingen</td>
                    <td className={styles.alignRight}>{formatBedrag(beginActiva['Overige vorderingen'])}</td>
                    <td></td>
                  </tr>
                )}

                <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

                {/* BTW - EXACT Morris */}
                {beginActiva['Omzetbelasting'] && beginActiva['Omzetbelasting'] > 0 && (
                  <tr>
                    <td>Omzetbelasting</td>
                    <td className={styles.alignRight}>{formatBedrag(beginActiva['Omzetbelasting'])}</td>
                    <td></td>
                  </tr>
                )}
                {beginActiva['Omzetbelasting'] && beginActiva['Omzetbelasting'] < 0 && (
                  <tr>
                    <td>Omzetbelasting</td>
                    <td></td>
                    <td className={styles.alignRight}>{formatBedrag(Math.abs(beginActiva['Omzetbelasting']))}</td>
                  </tr>
                )}
                {beginPassiva['Omzetbelasting'] && beginPassiva['Omzetbelasting'] > 0 && (
                  <tr>
                    <td>Omzetbelasting</td>
                    <td></td>
                    <td className={styles.alignRight}>{formatBedrag(beginPassiva['Omzetbelasting'])}</td>
                  </tr>
                )}
                {(beginPassiva['BTW privé gebruik auto'] || beginPassiva['Extra BTW auto']) && (
                  <tr>
                    <td>BTW privé gebruik auto</td>
                    <td></td>
                    <td className={styles.alignRight}>
                      {formatBedrag(beginPassiva['BTW privé gebruik auto'] || beginPassiva['Extra BTW auto'] || 0)}
                    </td>
                  </tr>
                )}

                <tr className={styles.spacerRow}><td colSpan={3}></td></tr>

                {/* Eigen vermogen */}
                <tr>
                  <td>Oudedagsreserve</td>
                  <td></td>
                  <td className={styles.alignRight}>
                    {formatBedrag(beginPassiva['Oudedagsreserve'] || beginPassiva['Oudedagreserve'] || 0)}
                  </td>
                </tr>
                <tr>
                  <td>Kapitaal</td>
                  <td></td>
                  <td className={styles.alignRight}>{formatBedrag(beginPassiva['Kapitaal'] || 0)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className={styles.separatorRow}>
                  <td></td>
                  <td className={styles.alignRight}>========</td>
                  <td className={styles.alignRight}>========</td>
                </tr>
                <tr className={styles.totaalRow}>
                  <td></td>
                  <td className={styles.alignRight}><strong>{formatBedrag(totaalBeginActiva)}</strong></td>
                  <td className={styles.alignRight}><strong>{formatBedrag(totaalBeginPassiva)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </BoekhoudingLayout>
  );
};

// Bijtelling berekening component - EXACT Morris JavaScript logica met peildatum
interface BijtellingTabelProps {
  auto: {
    id: number;
    naam: string;
    catalogusprijs: number;
    bouwjaar: number;
    start_datum: string | null;
    eind_datum: string | null;
    percentage_override: number | null;
  };
  jaar: number;
  peildatum: string;  // YYYY-MM-DD - EXACT Morris
}

const BijtellingTabel = ({ auto, jaar, peildatum }: BijtellingTabelProps) => {
  const catalogus = auto.catalogusprijs;
  const bouwjaar = auto.bouwjaar;
  const leeftijd = jaar - bouwjaar;
  const percentage = auto.percentage_override || (leeftijd >= 5 ? 1.5 : 2.7);

  // Bijtelling 22% (inkomstenbelasting)
  const bijtelling22 = catalogus * 0.22;

  // Helper functie om datum lokaal te parsen - EXACT Morris
  const parseLocalDate = (str: string) => {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // Bereken dagen met peildatum - EXACT Morris
  const jaarStart = new Date(jaar, 0, 1);
  const jaarEind = new Date(jaar, 11, 31);
  const peil = peildatum ? parseLocalDate(peildatum) : jaarEind;
  const startDatum = auto.start_datum ? parseLocalDate(auto.start_datum) : jaarStart;
  const eindDatum = auto.eind_datum ? parseLocalDate(auto.eind_datum) : jaarEind;

  const effectiefStart = startDatum < jaarStart ? jaarStart : startDatum;
  // Gebruik peildatum als maximum einddatum - EXACT Morris
  let effectiefEind = eindDatum > jaarEind ? jaarEind : eindDatum;
  if (peil < effectiefEind) effectiefEind = peil;

  const dagenInJaar = Math.round((jaarEind.getTime() - jaarStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const dagen = Math.max(0, Math.round((effectiefEind.getTime() - effectiefStart.getTime()) / (1000 * 60 * 60 * 24)) + 1);

  // Pro rata bijtelling
  const proRataBijtelling = bijtelling22 * (dagen / dagenInJaar);

  // BTW correctie
  const jaarBtw = catalogus * (percentage / 100);
  const proRataBtw = jaarBtw * (dagen / dagenInJaar);

  const formatBedrag = (bedrag: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(bedrag);
  };

  return (
    <table className={styles.balansTable}>
      <tbody>
        <tr>
          <td>Fiscale waarde</td>
          <td className={styles.alignRight}>{formatBedrag(catalogus)}</td>
        </tr>
        <tr>
          <td>Bijtelling 22% over waarde</td>
          <td className={styles.alignRight}>{formatBedrag(bijtelling22)}</td>
        </tr>
        <tr className={styles.spacerRow}><td colSpan={2}></td></tr>
        <tr>
          <td>{dagen} dagen</td>
          <td className={styles.alignRight}>{formatBedrag(proRataBijtelling)}</td>
        </tr>
        <tr className={styles.spacerRow}><td colSpan={2}></td></tr>
        <tr>
          <td><strong>BTW privé gebruik auto</strong></td>
          <td className={styles.alignRight}><strong>{formatBedrag(proRataBtw)}</strong></td>
        </tr>
      </tbody>
    </table>
  );
};

export default Balans;
